import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { socket } from 'api';
import { user } from 'services/user';
import { OptionsToggle, Confirm, Reload, FilledButton } from 'libs/ui';
import OptionsCam from './options-cam/options-cam';
import { OptionsCamPoly } from './options-cam-poly/options-cam-poly';
import { ControlPlate } from './control-plate/control-plate';
import { OptionReceipt } from './option-receipt/option-receipt';
import { Items, Wrapper, Left, Right, ButtonPlace, Offline } from './options.styled';

export const Options = ({ id }) => {
  const { t } = useTranslation();
  const status = useSelector((store) => store.equipment[id] ?? {});
  const [confirmReconc, setConfirmReconc] = useState(false);
  const { mode } = status;
  const offline = mode === 'offline';

  /**
   * get node
   * @returns
   */
  const getNode = () => {
    return window.service.call('getNode', id);
  };

  const node = getNode();
  const isEntry = node?.rack_type === 'entry';

  /**
   * send socket msg
   * @param {*} message
   * @param {*} params
   */
  const sendControlMsg = (message, params) => {
    const data = Object.assign({ target: id, user_id: user.getId() }, params);
    socket.call(message, data);
  };

  // update state
  useEffect(() => {
    sendControlMsg('get_state');
  }, []);

  /**
   * get toggles
   * @returns
   */
  const getItems = () => {
    const out = [];
    if (!node) {
      return out;
    }
    const { rack_type } = node;

    // all
    out.push(
      <OptionsToggle
        key={'settings.1'}
        sendControlMsg={sendControlMsg}
        title={t('checkboxes.Проезд по РФИД без поиска парковки')}
        name="settings.force_passage_by_rfid"
        state={status}
      />,
    );
    out.push(
      <OptionsToggle
        key={'settings.2'}
        sendControlMsg={sendControlMsg}
        title={t('checkboxes.Проезд по БСК без поиска парковки')}
        name="settings.force_passage_by_scard"
        state={status}
      />,
    );
    out.push(
      <OptionsToggle
        key={'settings.3'}
        sendControlMsg={sendControlMsg}
        title={t('checkboxes.Проезд по банковским картам')}
        name="settings.allow_passage_by_bcard"
        state={status}
      />,
    );

    // leave
    if (rack_type === 'leave' || rack_type === 'zone') {
      out.push(
        <OptionsToggle
          key={'settings.3a'}
          sendControlMsg={sendControlMsg}
          title={t('checkboxes.Проезд по билетам')}
          name="settings.enable_receipts"
          saveAsParam={true}
          state={status}
        />,
      );
      out.push(
        <OptionsToggle
          key={'settings.4'}
          sendControlMsg={sendControlMsg}
          title={t('checkboxes.Чтение QR с чека оплаты на стойке')}
          name="settings.allow_passage_by_qrcode"
          state={status}
        />,
      );
    }

    // zone or leave
    if (rack_type === 'zone' || rack_type === 'leave') {
      out.push(
        <OptionsToggle
          key={'settings.5'}
          sendControlMsg={sendControlMsg}
          title={t('checkboxes.Автоматическое разрешение проезда по ГРЗ')}
          name="settings.auto_allow_travel"
          state={status}
        />,
      );
    }

    // zone or entry
    if (rack_type === 'zone' || rack_type === 'entry') {
      out.push(
        <OptionsToggle
          key={'settings.6'}
          sendControlMsg={sendControlMsg}
          title={t('checkboxes.Блокировать при отсутствии мест в зоне')}
          name="settings.close_by_no_free_places"
          state={status}
        />,
      );
    }

    //  entry
    if (rack_type === 'entry') {
      out.push(
        <OptionsToggle
          key={'settings.7'}
          sendControlMsg={sendControlMsg}
          title={t('checkboxes.Въезд без билета')}
          name="settings.allow_passage_by_plate"
          saveAsParam={true}
          state={status}
        />,
      );
    }

    //  entry
    if (rack_type === 'entry') {
      out.push(
        <OptionsToggle
          key={'settings.8'}
          sendControlMsg={sendControlMsg}
          title={t('checkboxes.Блокировать при несовпадении класса ТС')}
          name="settings.check_mean_vehicle_class"
          saveAsParam={true}
          state={status}
        />,
      );
    }
    return out;
  };

  const showConfirmReconc = () => {
    setConfirmReconc(true);
  };

  const closeConfirm = () => {
    setConfirmReconc(false);
  };

  const reconciliation = () => {
    sendControlMsg('reconciliation');
    closeConfirm();
  };

  const getAdditionalCmp = () => {
    const node = getNode();
    return node?.rack_type === 'zone' || node?.rack_type === 'leave' ? (
      <ButtonPlace>
        <FilledButton disabled={confirmReconc} className="btn" onClick={showConfirmReconc}>
          {t('buttons.Сверка итогов')}
        </FilledButton>
        {confirmReconc && (
          <Confirm
            close={closeConfirm}
            action={reconciliation}
            title={t('windows.Провести сверку итогов?')}
            actionTitle={<>&nbsp; &nbsp; {t('buttons.Ок')} &nbsp; &nbsp;</>}
          />
        )}
      </ButtonPlace>
    ) : null;
  };

  let cl2 = offline ? 'offline1' : !mode ? 'loading1' : '';

  const withLimit = status.check_vehicle_plate_enable !== undefined;
  const limitEnabled = status.check_vehicle_plate_enable;
  const limitValue = status.check_vehicle_plate_distance;

  const items = getItems();
  const add = getAdditionalCmp();
  const polygonalCam = status?.recognition?.polygon !== undefined;
  const withReceipt = user.haveRight('receipt_template.edit');

  return (
    <Wrapper>
      <Left>
        {polygonalCam ? (
          <OptionsCamPoly sendControlMsg={sendControlMsg} id={id} add={add} />
        ) : (
          <OptionsCam status={status} sendControlMsg={sendControlMsg} id={id} add={add} />
        )}
      </Left>
      <Right className={cl2} $status={t('clarifications.отключен')}>
        {withLimit && <ControlPlate sendControlMsg={sendControlMsg} limitEnabled={limitEnabled} limitValue={limitValue} />}

        {items.length > 0 && <Items>{items}</Items>}

        {withReceipt && isEntry && <OptionReceipt sendControlMsg={sendControlMsg} templateId={status?.printer?.receipt_template_id} />}

        <div style={{ padding: '0 10px' }}>
          <Reload id={id} sendControlMsg={sendControlMsg} />
        </div>
      </Right>

      {offline && (
        <Offline>
          <span className="dot" />
          <span className="label">{t('clarifications.отключен')}</span>
        </Offline>
      )}
    </Wrapper>
  );
};
